import { Link, useStaticQuery, graphql } from "gatsby";
import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Image from "./image.js";
import logoSm from "../images/ep_mobile.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faInstagram,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { useEffect } from "react";
import DinteroPayments from "./dintero-payments.js";

const FooterWrapper = styled.footer`
  padding-inline: 2rem;
  background-color: ${({ theme }) =>
    theme.colors.footerBackgroundTop || `var(--color-secondary)`};
  color: ${({ theme }) => theme.colors.footerTextTop || `var(--color-black)`};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1rem;
  }
`;

const FooterInnerWrapper = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding-block: 4rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: flex;
    flex-direction: column;
    padding: 3rem 0 2rem 0;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-bottom: 0;
  }
`;

const Logo = styled(Image)`
  margin-bottom: 1rem;
  height: 105px !important;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    height: 60px !important;
  }
`;

const Label = styled.p`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  margin-top: 0;
  font-family:var(--font-headings); "sans-serif";
`;

const Links = styled.div`
  ul {
    padding-left: 0;
    list-style: none;
    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      margin-block: 0.5rem;
    }
    li {
      padding-block: 0.5rem;
      a {
        text-decoration: none;
        @media (hover: hover) {
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-block: 1rem;
  }
`;

const FooterBottom = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  border-top: 2px solid #e8e8e8b4;
  display: flex;
  justify-content: space-between;
  align-content: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-direction: column-reverse;
  }
`;

const Info = styled.div`
  padding-block: 2rem;

  p {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }
  a {
    text-decoration: none;
    font-size: 0.9rem;
    @media (hover: hover) {
      &:hover {
        opacity: 0.6;
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding-block: 1rem;
  }
`;

const SocialMedia = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
  padding-top: 0.2rem !important;
  padding-left: 0 !important;
  font-size: 22px !important;
  a {
    color: var(--color-white) !important;
    background-color: var(--color-black) !important;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const ExternalLinks = styled.a`
  padding-block: 0.2em;
  color: var(--color-white);
  font-family: var(--font-paragraph);
  text-decoration: none;
  font-size: 0.9rem;
  letter-spacing: 1px;
  white-space: nowrap;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function Footer({ staticQueryData }) {
  const [footerMenu, setFooterMenu] = useState([]);
  const { footerNavPrivate, footerNavBusiness, wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment FooterNav on WpMenuItem {
        order
        id
        url
        label
        target
      }

      query FooterQuery {
        footerNavPrivate: wpMenu(locations: { eq: FOOTER_NAV }) {
          menuItems {
            nodes {
              ...FooterNav
              childItems {
                nodes {
                  ...FooterNav
                }
              }
              parentId
            }
          }
        }
        footerNavBusiness: wpMenu(locations: { eq: FOOTER_NAV_BUSINESS }) {
          menuItems {
            nodes {
              ...FooterNav
              childItems {
                nodes {
                  ...FooterNav
                }
              }
              parentId
            }
          }
        }
        wp {
          generalSettings {
            title
          }
          crbThemeOptions {
            crbAddress
            crbEmail
            crbPhone
            crbOrgno
            crbSocialmedia {
              icon
              url
            }
            crbLogoFooter {
              media {
                sourceUrl
                altText
                focalPoint {
                  objectPosition
                }
                localFile {
                  publicURL
                  childImageSharp {
                    gatsbyImageData(
                      height: 50
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
          }
        }
      }
    `);

  const checkPrivateOrBusiness = () => {
    const privateOrBusiness = localStorage.getItem("private-business");
    if (privateOrBusiness === "business") {
      setFooterMenu(footerNavBusiness);
    } else {
      setFooterMenu(footerNavPrivate);
    }
  };

  useEffect(() => {
    checkPrivateOrBusiness();
    window.addEventListener("storage", checkPrivateOrBusiness);

    const script = document.createElement("script");
    script.src = process.env.GATSBY_CONSENT_SCRIPT_URL;
    script.crossOrigin = "anonymous";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      removeEventListener("storage", checkPrivateOrBusiness);
    };
  }, []);

  return (
    <FooterWrapper
      itemScope
      itemType="http://schema.org/Organization"
      aria-label="footer"
    >
      <FooterInnerWrapper>
        <div>
          <meta itemProp="name" content={wp.generalSettings?.title} />
          {wp?.crbThemeOptions?.crbLogoFooter?.media ? (
            <Logo
              itemProp="image"
              image={wp.crbThemeOptions.crbLogoFooter.media ?? ""}
              src={wp.crbThemeOptions.crbLogoFooter.media.sourceUrl ?? ""}
              alt="Logotype"
            />
          ) : (
            <Logo itemProp="image" src={logoSm} alt="Logotype" />
          )}
          {wp?.crbThemeOptions?.crbAddress?.length > 0 && (
            <p itemProp="address">{wp.crbThemeOptions.crbAddress}</p>
          )}
          {wp?.crbThemeOptions?.crbPhone?.length > 0 && (
            <p itemProp="telephone">
              <a href={`tel:${wp.crbThemeOptions.crbPhone}`}>
                {wp.crbThemeOptions.crbPhone}
              </a>
            </p>
          )}
          {wp?.crbThemeOptions?.crbEmail?.length > 0 && (
            <p itemProp="email">
              <a href={`mailto:${wp.crbThemeOptions.crbEmail}`}>
                {wp.crbThemeOptions.crbEmail}
              </a>
            </p>
          )}
          {wp?.crbThemeOptions?.crbOrgno?.length > 0 && (
            <p itemProp="orgno">Org.nr: {wp.crbThemeOptions.crbOrgno}</p>
          )}

          <SocialMedia>
            {wp?.crbThemeOptions.crbSocialmedia.map((socialMedia, index) => (
              <ExternalLinks
                aria-label="social-media"
                key={index}
                href={socialMedia.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {socialMedia.icon === "fab facebook-f" ? (
                  <FontAwesomeIcon icon={faFacebookF} />
                ) : socialMedia.icon === "fab instagram" ? (
                  <FontAwesomeIcon icon={faInstagram} />
                ) : socialMedia.icon === "fab youtube" ? (
                  <FontAwesomeIcon icon={faYoutube} />
                ) : null}
              </ExternalLinks>
            ))}
          </SocialMedia>
        </div>
        {footerMenu?.menuItems?.nodes
          ?.filter((node) => node.parentId === null)
          ?.map((node) => (
            <Links key={node.id}>
              <Label>{node.label}</Label>
              {node.childItems?.nodes && (
                <ul>
                  {node.childItems?.nodes?.map((childNode) => {
                    return childNode.target != null ? (
                      <li key={childNode.id}>
                        <a href={childNode.url} target={childNode.target}>
                          {childNode.label}{" "}
                          <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                        </a>
                      </li>
                    ) : (
                      <li key={childNode.id}>
                        <Link to={childNode.url}>{childNode.label}</Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Links>
          ))}
      </FooterInnerWrapper>

      <FooterBottom>
        <Info>
          <p>Design og kode:</p>
          <a href="https://enklerevalg.no/" target="_blank" rel="noreferrer">
            Mediebyrået Enklere Valg
          </a>
        </Info>
        <DinteroPayments padding={"2rem 0"} variant={"black"} isBig={true} />
      </FooterBottom>
    </FooterWrapper>
  );
}

Footer.propTypes = {
  staticQueryData: PropTypes.object,
};
